import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueue } from '../hooks/useQueue';

const API_BASE = import.meta.env.VITE_API_URL || `http://${window.location.hostname}:7070`;
import { usePushSubscription } from '../hooks/usePushSubscription';
import JoinScreen from '../components/JoinScreen';
import QueueScreen from '../components/QueueScreen';
import RecoveryScreen from '../components/RecoveryScreen';
import ReconnectingBanner from '../components/ReconnectingBanner';
import ReviewPromptScreen from '../components/ReviewPromptScreen';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function QueuePage() {
  const { shopId } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const [showRecovery, setShowRecovery] = useState(false);
  const [shopName, setShopName] = useState(null);
  const [shopCalledTitle, setShopCalledTitle] = useState('');
  const [shopCalledBody, setShopCalledBody] = useState('');
  const [reviewPromptEnabled, setReviewPromptEnabled] = useState(false);
  const [acceptingNewCustomers, setAcceptingNewCustomers] = useState(true);
  const [reviewUrl, setReviewUrl] = useState('');
  const [reviewThreshold, setReviewThreshold] = useState(4);
  const [reviewDismissed, setReviewDismissed] = useState(false);
  const [services, setServices] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState(searchParams.get('service') || null);
  const [queueStatus, setQueueStatus] = useState(null);
  const [dynamicQr, setDynamicQr] = useState(false);
  const [tokenValid, setTokenValid] = useState(null); // null=loading, true=ok, false=expired

  const resolvedShopId = UUID_RE.test(shopId) ? shopId : null;
  const qrToken = searchParams.get('t');

  useEffect(() => {
    if (!resolvedShopId) return;
    fetch(`${API_BASE}/api/v1/public/shops/by-id/${resolvedShopId}`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data) {
          setShopName(data.name);
          if (data.calledTitle) setShopCalledTitle(data.calledTitle);
          if (data.calledBody) setShopCalledBody(data.calledBody);
          setDynamicQr(!!data.dynamicQrEnabled);
          setReviewPromptEnabled(!!data.reviewPromptEnabled && !!data.reviewUrl);
          setReviewUrl(data.reviewUrl || '');
          setAcceptingNewCustomers(data.acceptingNewCustomers !== false);
          if (typeof data.reviewThreshold === 'number' && data.reviewThreshold >= 1 && data.reviewThreshold <= 5) {
            setReviewThreshold(data.reviewThreshold);
          }
          if (data.dynamicQrEnabled && qrToken) {
            fetch(`${API_BASE}/api/v1/public/shops/${resolvedShopId}/validate-qr/${qrToken}`)
              .then((r) => r.ok ? r.json() : { valid: false })
              .then((d) => setTokenValid(d.valid))
              .catch(() => setTokenValid(false));
          } else if (data.dynamicQrEnabled && !qrToken) {
            setTokenValid(false);
          } else {
            setTokenValid(true);
          }
        }
      })
      .catch(() => {});
    fetch(`${API_BASE}/api/v1/public/shops/${resolvedShopId}/services`)
      .then((r) => r.ok ? r.json() : [])
      .then((data) => {
        setServices(data);
        if (!selectedServiceId && data.length === 1) setSelectedServiceId(data[0].id);
      })
      .catch(() => {});
    fetch(`${API_BASE}/api/v1/public/shops/${resolvedShopId}/queue-status`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setQueueStatus(data); })
      .catch(() => {});
  }, [resolvedShopId]);

  const isDemo = resolvedShopId === '00000000-0000-0000-0000-000000000002';
  const { phase, myEntry, graceHold, deskLabel, wsStatus, retryAttempt, serverError, session, join, leave, recoverWithPin } = useQueue(resolvedShopId, { ephemeral: isDemo });
  usePushSubscription(session?.ticketUuid, resolvedShopId, phase !== 'idle' && phase !== 'completed');

  const showBanner = wsStatus === 'disconnected' && phase !== 'idle' && phase !== 'completed';

  if (!resolvedShopId) {
    return (
      <Layout>
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
          <span style={{ fontSize: '3rem' }}>🏷️</span>
          <h1 style={titleStyle}>{t('notFound.title')}</h1>
          <p style={subtitleStyle}>{t('notFound.body')}</p>
        </div>
      </Layout>
    );
  }

  const header = shopName ? (
    <div style={{ textAlign: 'center', marginBottom: 16 }}>
      <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#F8FAFC' }}>{shopName}</h2>
    </div>
  ) : null;

  // ── Completed ─────────────────────────────────────────────────────────────
  if (phase === 'completed') {
    const showReview = reviewPromptEnabled && reviewUrl && !reviewDismissed;
    return (
      <Layout>
        {header}
        {showReview ? (
          <ReviewPromptScreen
            reviewUrl={reviewUrl}
            threshold={reviewThreshold}
            onDismiss={() => setReviewDismissed(true)}
          />
        ) : (
          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: '5rem' }}>✅</div>
            <h1 style={titleStyle}>{t('completed.title')}</h1>
            <p style={subtitleStyle}>{t('completed.body')}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={primaryBtnStyle}
            >
              {t('completed.backButton')}
            </button>
          </div>
        )}
      </Layout>
    );
  }

  // ── Server error ─────────────────────────────────────────────────────────
  if (serverError) {
    return (
      <Layout>
        {header}
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#EF4444', fontWeight: 600 }}>
            {t(`error.${serverError}`, t('error.generic'))}
          </p>
        </div>
      </Layout>
    );
  }

  // ── Recovery ─────────────────────────────────────────────────────────────
  if (showRecovery) {
    return (
      <Layout>
        {header}
        <RecoveryScreen
          onRecover={(pin) => {
            const result = recoverWithPin(pin);
            if (result === 'ok') setShowRecovery(false);
            return result;
          }}
          onBack={() => setShowRecovery(false)}
        />
      </Layout>
    );
  }

  // ── Expired / missing QR token ────────────────────────────────────────────
  if (tokenValid === false && phase === 'idle') {
    return (
      <Layout>
        {header}
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', padding: '2rem 0' }}>
          <span style={{ fontSize: '3rem' }}>⏰</span>
          <h1 style={titleStyle}>{t('queue.qrExpired')}</h1>
          <p style={subtitleStyle}>{t('queue.qrExpiredHint')}</p>
        </div>
      </Layout>
    );
  }

  // ── Shop is not accepting new customers today ────────────────────────────
  // Existing customers (phase !== 'idle') still see their queue state — this
  // screen only applies to someone trying to join fresh.
  if (!acceptingNewCustomers && phase === 'idle') {
    return (
      <Layout>
        {header}
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', padding: '2rem 0' }}>
          <span style={{ fontSize: '3rem' }}>🌙</span>
          <h1 style={titleStyle}>{t('queue.closedTitle')}</h1>
          <p style={subtitleStyle}>{t('queue.closedBody')}</p>
        </div>
      </Layout>
    );
  }

  // ── Joining / In-queue / Called / Grace-hold ──────────────────────────────
  if (phase !== 'idle') {
    return (
      <Layout>
        {header}
        {showBanner && <ReconnectingBanner attempt={retryAttempt} />}
        <QueueScreen
          phase={phase}
          myEntry={myEntry}
          graceHold={graceHold}
          deskLabel={deskLabel}
          session={session}
          onLeave={leave}
          calledTitle={shopCalledTitle}
          calledBody={shopCalledBody}
        />
      </Layout>
    );
  }

  // ── Queue preview bar ─────────────────────────────────────────────────────
  const previewBar = queueStatus ? (
    <div style={previewCardStyle}>
      {queueStatus.totalQueueSize === 0 ? (
        <span style={{ color: '#94A3B8', fontSize: '0.9rem' }}>{t('queue.preview.empty')}</span>
      ) : (
        <span style={{ color: '#94A3B8', fontSize: '0.9rem' }}>
          <span style={{ color: '#06B6D4', fontWeight: 700 }}>{queueStatus.totalQueueSize}</span>
          {' '}
          {queueStatus.totalQueueSize === 1 ? t('queue.preview.oneWaiting') : t('queue.preview.peopleWaiting', { count: queueStatus.totalQueueSize })}
          {' \u00B7 '}
          {t('queue.preview.estimatedWait', { min: queueStatus.estimatedWaitMinutes })}
        </span>
      )}
    </div>
  ) : null;

  // ── Helper: per-service queue size from status ──────────────────────────
  const serviceSizeMap = {};
  if (queueStatus?.services) {
    for (const s of queueStatus.services) {
      serviceSizeMap[s.serviceId] = s.queueSize;
    }
  }

  // ── Service selection (if shop has multiple services) ─────────────────────
  if (services.length > 1 && !selectedServiceId) {
    return (
      <Layout>
        {header}
        {previewBar}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, width: '100%' }}>
          <h1 style={titleStyle}>{t('queue.chooseService')}</h1>
          <p style={subtitleStyle}>{t('queue.chooseServiceSub')}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
            {services.map((svc) => {
              const count = serviceSizeMap[svc.id] ?? null;
              return (
                <button
                  key={svc.id}
                  type="button"
                  onClick={() => setSelectedServiceId(svc.id)}
                  style={serviceBtnStyle}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontWeight: 700, fontSize: '1rem', color: '#F8FAFC' }}>{svc.name}</div>
                    {count != null && (
                      <span style={serviceBadgeStyle}>{count}</span>
                    )}
                  </div>
                  {svc.description && <div style={{ fontSize: '0.85rem', color: '#64748B', marginTop: 4 }}>{svc.description}</div>}
                </button>
              );
            })}
          </div>
        </div>
      </Layout>
    );
  }

  // ── Idle: join screen ────────────────────────────────────────────────────
  const serviceName = services.find(s => s.id === selectedServiceId)?.name;
  return (
    <Layout>
      {header}
      {previewBar}
      {serviceName && services.length > 1 && (
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: '0.85rem', color: '#7C3AED', fontWeight: 600 }}>{serviceName}</span>
          <button type="button" onClick={() => setSelectedServiceId(null)}
            style={{ background: 'none', border: 'none', color: '#94A3B8', fontSize: '0.8rem', cursor: 'pointer', marginLeft: 8 }}>
            {t('queue.changeService')}
          </button>
        </div>
      )}
      <JoinScreen
        onJoin={({ avatarId, avatarName, turnstileToken }) => join({ avatarId, avatarName, serviceId: selectedServiceId || '', turnstileToken, qrToken: qrToken || '' })}
        onRecover={() => setShowRecovery(true)}
      />
    </Layout>
  );
}

function Layout({ children }) {
  return (
    <div style={{
      minHeight: '100dvh',
      background: '#0F172A',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      padding: '32px 16px',
    }}>
      <div style={{
        background: '#1E293B',
        borderRadius: 20,
        boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
        padding: '32px 24px',
        width: '100%',
        maxWidth: 420,
      }}>
        {children}
      </div>
    </div>
  );
}

const titleStyle = { fontSize: '1.75rem', fontWeight: 700, margin: 0, color: '#F8FAFC' };
const subtitleStyle = { fontSize: '0.95rem', color: '#94A3B8', margin: 0 };
const primaryBtnStyle = {
  fontSize: '1rem', fontWeight: 600, padding: '13px', borderRadius: 12,
  border: 'none', background: '#7C3AED', color: '#fff', cursor: 'pointer',
  width: '100%',
};
const serviceBtnStyle = {
  textAlign: 'left', padding: '16px', borderRadius: 12,
  border: '1.5px solid #334155', background: '#1E293B', cursor: 'pointer',
  transition: 'border-color 0.15s', width: '100%',
};
const previewCardStyle = {
  background: '#1E293B', border: '1px solid #334155', borderRadius: 10,
  padding: '10px 14px', textAlign: 'center', marginBottom: 16,
};
const serviceBadgeStyle = {
  background: 'rgba(6,182,212,0.15)', color: '#06B6D4', fontWeight: 700,
  fontSize: '0.8rem', borderRadius: 10, padding: '2px 8px', minWidth: 24,
  textAlign: 'center', display: 'inline-block',
};
